// src/components/MAP/VideoTab.js
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./VideoTab.css";
import VideoPlayer from "../VIDEO/VideoPlayer";
import VideoStory from "../Tabs/VideoStory";
import FilterControls from './components/FilterControls';
import { useIconFilters } from './hooks/useIconFilters';
import { useMapInitialization } from './hooks/useMapInitialization';
import { useTimeSync } from './hooks/useTimeSync';
import { useTrajectoryHighlight } from './hooks/useTrajectoryHighlight';
import { useAppStore } from "../../store/appStore";
import { useSharedState } from './hooks/useSharedState';

// Add these imports
import { throttle } from "./utils/performance";
import videoSyncController from './controllers/VideoSyncController';
import tabSyncCoordinator from './coordinators/TabSyncCoordinator';

const VideoTab = forwardRef(({ csvFiles, onTimeUpdate, onTrackObject, tabIndex = 2 }, ref) => {
  // 从Zustand获取全局状态
  const sharedState = useSharedState();  
  const activeRobotId = useAppStore(state => state.selection.activeRobotId);
  const activeEventId = useAppStore(state => state.selection.activeEventId);
  const currentTime = useAppStore(state => state.videoState.currentTime);
  const isPlaying = useAppStore(state => state.videoState.isPlaying);
  const videoUrl = useAppStore(state => state.videoUrl);
  const timeframe = useAppStore(state => state.timeframe);
  const processingUpdate = useAppStore(state => state.metadata.processingUpdate);
  // 获取操作方法
  const updateVideoState = useAppStore(state => state.updateVideoState);
  const seekTo = useAppStore(state => state.seekTo);

  // 使用共享钩子初始化地图 - 使用与AnalysisView相同的地图ID
  const {
    mapInstance,
    routes,
    setRoutes,
    timelineData,
    polylinesRef,
    backgroundPolylinesRef,
    markersRef,
    customMarkersRef,
    routeIconsManager,
    setCurrentVisibleIcons,
    clearMap,
    rebuildMap
  } = useMapInitialization("video-tab-map", csvFiles, timeframe);
  
  // 时间同步钩子
  const { handleSliderChange } = useTimeSync(
    timelineData, 
    polylinesRef, 
    markersRef, 
    mapInstance, 
    routes
  );
  
  // 图标过滤钩子
  const { visibleTypes, toggleIconType } = useIconFilters(customMarkersRef);
  
  // 本地状态 - 保留地图展开状态，但不再使用地图延迟显示
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  
  // 创建refs
  const videoRef = useRef(null);
  const isComponentMounted = useRef(true);
  
  // 使用轨迹高亮钩子 - 与AnalysisView相同的简化版本
  const {
    highlightTrajectory,
    clearHighlight
  } = useTrajectoryHighlight({
    mapInstance,
    backgroundPolylinesRef,
    markersRef,
    activeRobotId,
    timeframe,
    onRobotSelect: (robotId, robotVideoUrl) => {
      // 更新全局状态，使用Zustand
      updateVideoState({ 
        robotId: robotId,
        videoUrl: robotVideoUrl
      }, 'videoTab');
    }
  });

  // 处理组件挂载/卸载
  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const unregister = videoSyncController.registerComponent(`tab_${tabIndex}`, {
      onTimeSync: (time) => {
        if (videoRef.current && Math.abs(videoRef.current.currentTime - time) > 0.2) {
          videoRef.current.currentTime = time;
        }
      },
      onPlay: () => {
        if (videoRef.current && videoRef.current.paused) {
          videoRef.current.play().catch(e => console.warn('Play failed:', e));
        }
      },
      onPause: () => {
        if (videoRef.current && !videoRef.current.paused) {
          videoRef.current.pause();
        }
      }
    });
    
    return () => {
      if (unregister) unregister();
    };
  }, [tabIndex]);
  
  // 处理对象跟踪
  const handleTrackObject = useCallback((trackData) => {
    if (!isComponentMounted.current || !onTrackObject) return;
    onTrackObject(trackData);
  }, [onTrackObject]);

  // 处理视频时间更新 - 与AnalysisView相似的方式
  const handleVideoTimeUpdate = useCallback(
    throttle((time) => {
      if (!isComponentMounted.current || processingUpdate) return;
      
      // Check if should respond to update
      if (!tabSyncCoordinator.shouldRespondToUpdate(tabIndex, 'timeUpdate')) return;
      
      // Avoid minor changes triggering updates
      if (Math.abs(time - currentTime) < 0.1) return;
      
      // Notify controller of time change
      videoSyncController.updateCurrentTime(time, `tab_${tabIndex}`);
      
      // Update global state
      updateVideoState({ time }, `videoTab_${tabIndex}`);
      
      // Call traditional callback
      if (onTimeUpdate) {
        onTimeUpdate(time);
      }
      
      // Update map for all routes
      handleSliderChange(time);
      
      // 简化高亮轨迹处理，使用AnalysisView中的方式
      if (activeRobotId && typeof highlightTrajectory === 'function') {
        highlightTrajectory(activeRobotId);
      }
    }, 50), // 50ms throttle
    [currentTime, handleSliderChange, onTimeUpdate, processingUpdate, updateVideoState, tabIndex, highlightTrajectory, activeRobotId]
  );

  // 视频选择处理
  const handleVideoSelect = useCallback(async (videoUrl, startTime, keepPlaying = false, robotId = null) => {
    if (!isComponentMounted.current || processingUpdate) return Promise.resolve();
    
    // 如果没有传递 robotId，尝试从 videoUrl 提取
    if (!robotId && videoUrl) {
      const robotIdMatch = videoUrl.match(/video(\d+)/);
      robotId = robotIdMatch ? parseInt(robotIdMatch[1]) : null;
    }
    
    // 如果仍然没有 robotId，使用当前激活的 robotId
    if (!robotId) {
      robotId = activeRobotId;
    }
    
    console.log(`[VideoTab] 公开方法 handleVideoSelect - URL=${videoUrl}, time=${startTime}, robotId=${robotId}, play=${keepPlaying}`);
    
    // 使用全局状态更新
    updateVideoState({
      robotId,
      videoUrl,
      time: startTime,
      isPlaying: keepPlaying
    }, 'videoTab_exposed');
    
    return Promise.resolve();
  }, [processingUpdate, activeRobotId, updateVideoState]);
  
  // 事件块点击处理 - 修改为与AnalysisView相似的方式
  const handleTimeBlockClick = useCallback((startTime, robotId, eventId) => {
    if (!isComponentMounted.current || processingUpdate) return;
    
    // Construct the correct video URL
    const timePart = timeframe === "11AM" ? "day" : "night";
    const videoUrl = `/${timePart}/videos/video${robotId}.mp4`;
    
    console.log(`VideoTab: Time block clicked - Time: ${startTime}, Robot: ${robotId}, Event: ${eventId}, Video: ${videoUrl}`);
    
    // Update global state with all required parameters
    updateVideoState({ 
      eventId: eventId,
      robotId: robotId,
      time: startTime,
      videoUrl: videoUrl
    }, 'videoTab');
  }, [processingUpdate, timeframe, updateVideoState]);

  // 移除复杂的地图调整逻辑，简化为AnalysisView中的处理方式
  useEffect(() => {
    if (!isComponentMounted.current || !mapInstance.current) return;
    
    // 确保地图大小正确
    mapInstance.current.invalidateSize();
    
    return () => {
      if (mapInstance.current) {
        // 清理任何地图事件监听器
      }
    };
  }, [mapInstance]);

  // 切换地图展开状态 - 保留这个功能因为它是UI相关的
  const handleToggleMapExpand = useCallback(() => {
    setIsMapExpanded(prev => !prev);
  }, []);
  
  // 移除图标大小调整功能，与AnalysisView保持一致
  
  // 更新图标可见性
  const updateIconsVisibility = useCallback((routeIndex, visible) => {
    if (!isComponentMounted.current) return;
    
    setRoutes((prev) => {
      // 构建新状态
      const newRoutes = {
        ...prev,
        [routeIndex]: {
          ...prev[routeIndex],
          visible: visible,
        },
      };

      // 更新路由图标
      if (routeIconsManager.current) {
        const routeIcons = routeIconsManager.current.getIconsForRoute(routeIndex);
        routeIcons.forEach((icon) => {
          if (customMarkersRef.current?.isTypeVisible(icon.type)) {
            customMarkersRef.current?.updateRouteIcons([icon], visible);
          }
        });
      }

      // 更新图层可见性
      const polyline = polylinesRef.current[routeIndex];
      const backgroundPolyline = backgroundPolylinesRef.current[routeIndex];
      const marker = markersRef.current[routeIndex];
      
      if (mapInstance.current) {
        // 轨迹线处理
        if (polyline) {
          if (visible) {
            if (!mapInstance.current.hasLayer(polyline)) {
              mapInstance.current.addLayer(polyline);
            }
          } else {
            if (mapInstance.current.hasLayer(polyline)) {
              mapInstance.current.removeLayer(polyline);
            }
          }
        }
        
        // 背景轨迹线处理
        if (backgroundPolyline) {
          if (visible) {
            if (!mapInstance.current.hasLayer(backgroundPolyline)) {
              mapInstance.current.addLayer(backgroundPolyline);
            }
          } else {
            if (mapInstance.current.hasLayer(backgroundPolyline)) {
              mapInstance.current.removeLayer(backgroundPolyline);
            }
          }
        }
        
        // 标记点处理
        if (marker) {
          if (visible) {
            if (!mapInstance.current.hasLayer(marker)) {
              mapInstance.current.addLayer(marker);
            }
          } else {
            if (mapInstance.current.hasLayer(marker)) {
              mapInstance.current.removeLayer(marker);
            }
          }
        }
      }
      
      return newRoutes;
    });
  }, [customMarkersRef, mapInstance, polylinesRef, backgroundPolylinesRef, markersRef, setRoutes]);

  // 处理地图容器点击 - 保留此功能因为它是功能相关的
  useEffect(() => {
    // 为地图容器添加事件委托处理器
    const handleMapContainerClick = (e) => {
      // 检查是否点击了Check Details链接
      if (e.target.classList.contains('map-event-link') || 
          e.target.closest('.map-event-link')) {
        
        // 获取最近的事件链接元素
        const linkElement = e.target.classList.contains('map-event-link') ? 
                            e.target : e.target.closest('.map-event-link');
        
        // 获取事件数据
        const robotId = parseInt(linkElement.getAttribute('data-robot-id'), 10);
        const eventId = parseInt(linkElement.getAttribute('data-event-id'), 10);
        const startTime = parseFloat(linkElement.getAttribute('data-start-time'));
        
        console.log(`VideoTab: Popup link clicked - Robot: ${robotId}, Event: ${eventId}, Time: ${startTime}`);
        
        // 使用handleTimeBlockClick处理点击
        handleTimeBlockClick(startTime, robotId, eventId);
      }
    };
    
    // 获取地图容器
    const mapContainer = document.getElementById('video-tab-map');
    if (mapContainer) {
      mapContainer.addEventListener('click', handleMapContainerClick);
    }
    
    // 清理函数
    return () => {
      if (mapContainer) {
        mapContainer.removeEventListener('click', handleMapContainerClick);
      }
    };
  }, [handleTimeBlockClick]);

  // 监听currentTime变化
  useEffect(() => {
    if (!isComponentMounted.current || processingUpdate || !timelineData.length) return;
    if (currentTime < 0) return;

    // 更新动态轨迹线
    handleSliderChange(currentTime);
  }, [currentTime, processingUpdate, timelineData.length, handleSliderChange]);

  // 添加这个useEffect来处理地图展开/折叠
useEffect(() => {
  if (!isComponentMounted.current || !mapInstance.current) return;
  
  // 延迟执行以确保DOM已更新
  setTimeout(() => {
    if (!isComponentMounted.current || !mapInstance.current) return;
    
    // 重新计算地图大小
    mapInstance.current.invalidateSize();
    
    // 调整地图视图以显示所有标记
    if (timelineData.length > 0) {
      // 收集所有路径点
      const allPoints = [];
      timelineData.forEach(({ routeLatLngs }) => {
        if (routeLatLngs && routeLatLngs.length > 0) {
          allPoints.push(...routeLatLngs);
        }
      });
      
      // 如果有点，则适应视图
      if (allPoints.length > 0) {
        const bounds = L.latLngBounds(allPoints);
        mapInstance.current.fitBounds(bounds, {
          padding: [50, 50],
          maxZoom: 18
        });
      }
    }
  }, 300);
}, [isMapExpanded, mapInstance, timelineData]);

  // 公开方法给父组件
  useImperativeHandle(ref, () => ({
    clearMap: () => {
      if (mapInstance.current) {
        clearMap();
      }
    },
    
    // 重建地图方法
    rebuildMap: () => {
      rebuildMap();
    },
    
    // 时间控制
    handleSliderChange: (value) => {
      if (!isComponentMounted.current || processingUpdate) return;
      updateVideoState({ time: value }, 'videoTab');
      handleSliderChange(value);
    },
    
    // 视频选择
    handleVideoSelect,
    
    // 事件块点击
    handleTimeBlockClick,
    
    // 图标可见性
    updateIconsVisibility,
    
    // 对象跟踪
    handleTrackObject,
    
    // 获取当前时间
    getCurrentTime: () => {
      return videoRef.current ? videoRef.current.currentTime : currentTime;
    },
    
    // 重置时间帧 - 使用全局状态
    resetTimeframe: (newTimeframe) => {
      if (!isComponentMounted.current) return;
      
      // 使用全局状态更新时间帧
      const appStore = useAppStore.getState();
      appStore.changeTimeframe(newTimeframe);
      
      // 清理自定义标记
      setCurrentVisibleIcons([]);
      if (customMarkersRef.current) {
        customMarkersRef.current.clear();
      }
    },
    
    // 禁用高亮
    disableHighlight: clearHighlight
  }));

  // 修改渲染结构，但保留原始的UI布局
  return (
    <div className="videotab-container">
      <div className="videotab-main-content">
        <div className="videotab-sidebar">
          <VideoStory
            robotId={activeRobotId || 1}
            onEventClick={handleTimeBlockClick}
            timeframe={timeframe}
            activeEventId={activeEventId}
            onTrackObject={handleTrackObject}
          />
        </div>
        
        <div className="videotab-video">
          <VideoPlayer
            ref={videoRef}
            isExpanded={true}
            videoUrl={videoUrl}
            onTimeUpdate={handleVideoTimeUpdate}
          />
        </div>
      </div>
      
      {/* 保留地图容器，但简化地图逻辑 */}
      <div className={`videotab-map-container ${isMapExpanded ? "expanded" : ""}`}>
        <div className="map-filter-controls">
          <FilterControls 
            visibleTypes={visibleTypes} 
            toggleIconType={toggleIconType}
          />
        </div>
        
        <div id="video-tab-map" className="videotab-map"></div>
        
        <button 
          className="map-expand-button" 
          onClick={handleToggleMapExpand}
          title={isMapExpanded ? "Collapse Map" : "Expand Map"}
        >
          {isMapExpanded ? "↙" : "↗"}
        </button>
      </div>
    </div>
  );
});

export default VideoTab;